import { enforceShape } from "@/src/lib/enforce-shape";
import { NotFoundError } from "@/src/lib/error";
import { logAndThrow, logger } from "@/src/lib/logging/logger";
import { withDurationMetric } from "@/src/lib/metrics";
import { hasPostgreSqlErrorCode, PostgreSqlErrorCode } from "@/src/lib/postgres-error-codes";
import { isNpiRegistryResult } from "@/src/lib/practitioner-registry-data";
import { withRetry } from "@/src/lib/retry";
import { customCamelCase, customSnakeCase, properCase } from "@/src/lib/string";
import { getSupabaseClient } from "@/src/lib/supabase";
import { PractitionerRow } from "@/src/lib/types/tables.types";
import { Group } from "@/src/models/group";
import { Practitioner, PractitionerRole, practitionerShape } from "@/src/models/practitioner";
import { BaseRepository } from "@/src/repo/base-repository";
import { ReceivingRosterCreateOptions } from "@/src/repo/receiving-roster-create";

export const PRACTITIONER_TABLE_NAME = "practitioner";

export const fullPractitionerSelect = `
  *
`;

export function mapPractitionerRowToPractitioner(practitionerRow: any): Practitioner {
  const mappedFields: Record<string, any> = {};

  for (const [key, value] of Object.entries(practitionerRow)) {
    if (key === "canonical_npi_number") {
      continue;
    }

    mappedFields[customCamelCase(key)] = value;
  }

  return {
    ...mappedFields,
  } as Practitioner;
}

function mapPractitionerToPractitionerRow(practitioner: Practitioner): PractitionerRow {
  const { ...rest } = practitioner;
  const cleanedPractitioner = enforceShape(rest, practitionerShape);

  const mappedFields: Record<string, any> = {};

  for (const [key, value] of Object.entries(cleanedPractitioner)) {
    mappedFields[customSnakeCase(key)] = value;
  }

  return {
    ...mappedFields,
  } as PractitionerRow;
}

function mapUniquePractitioners(practitionerRows: PractitionerRow[]): Record<string, Practitioner> {
  let seenNpiAndRole: Set<string> = new Set();

  return practitionerRows.reduce((acc: Record<string, Practitioner>, practitioner: PractitionerRow) => {
    const npiRoleKey = `${practitioner.npi_number}|${practitioner.role}`;

    if (seenNpiAndRole.has(npiRoleKey)) {
      return acc;
    }

    seenNpiAndRole.add(npiRoleKey);

    acc[practitioner.id] = mapPractitionerRowToPractitioner(practitioner);

    return acc;
  }, {});
}

export class PractitionerRepository extends BaseRepository<Practitioner, PractitionerRow> {
  constructor() {
    super({
      tableName: PRACTITIONER_TABLE_NAME,
      selectClause: fullPractitionerSelect,
      mapRowToModel: mapPractitionerRowToPractitioner,
      mapModelToRow: mapPractitionerToPractitionerRow,
    });
  }
}

export const practitionerRepository = new PractitionerRepository();
export const getPractitioner = practitionerRepository.get.bind(practitionerRepository);
export const getPractitionerByDisplayId = practitionerRepository.getByDisplayId.bind(practitionerRepository);
export const getAllPractitionersDB = practitionerRepository.getAll.bind(practitionerRepository);
export const insertPractitioner = practitionerRepository.insert.bind(practitionerRepository);

async function updatePractitionerImpl(
  id: string,
  partialModel: Partial<Practitioner>,
  group: Group,
  allowReceivingWrite: boolean
): Promise<Practitioner> {
  const existing = await getPractitioner(id, group);

  if (!existing) {
    throw new NotFoundError("Practitioner not found");
  }

  if (existing.role === PractitionerRole.RECEIVING_PROVIDER && !allowReceivingWrite) {
    logger.info("updatePractitioner: receiving write skipped (non-SOR caller)", {
      id,
      groupId: group.id,
    });

    return existing;
  }

  return practitionerRepository.update(id, partialModel, group);
}

export async function updatePractitioner(
  id: string,
  partialModel: Partial<Practitioner>,
  group: Group
): Promise<Practitioner> {
  return updatePractitionerImpl(id, partialModel, group, false);
}

export async function updatePractitionerFromSor(
  id: string,
  partialModel: Partial<Practitioner>,
  group: Group
): Promise<Practitioner> {
  return updatePractitionerImpl(id, partialModel, group, true);
}

/**
 * Create a practitioner
 * @param practitioner - The practitioner to create
 * @param group - The group to create the practitioner for
 * @returns The created practitioner
 */
export const createPractitioner = withDurationMetric(async function createPractitioner(
  practitioner: Practitioner,
  group: Group
): Promise<Practitioner> {
  if (!group) {
    throw new Error("Group not provided for practitioner");
  }

  const practitionerToSave: Practitioner = {
    ...practitioner,
    groupId: practitioner.groupId ?? group.id,
    givenName: practitioner.givenName ? properCase(practitioner.givenName) : practitioner.givenName,
    familyName: practitioner.familyName ? properCase(practitioner.familyName) : practitioner.familyName,
    middleName: practitioner.middleName ? properCase(practitioner.middleName) : practitioner.middleName,
  };

  const createdPractitioner = await insertPractitioner(practitionerToSave, group);

  return createdPractitioner;
}, "createPractitioner");

/**
 * Get a practitioner with matching values
 * @param practitioner - The practitioner to get
 * @param group - The group to get the practitioner from
 * @returns The practitioner
 */
export const getPractitionerWithMatchingValues = withDurationMetric(async function getPractitionerWithMatchingValues(
  practitioner: Practitioner,
  group: Group,
  expectedRole?: PractitionerRole
): Promise<Practitioner | null> {
  if (!practitioner.npiNumber) {
    return null;
  }

  const npiNumber = practitioner.npiNumber;
  const { data: practitionerRows, error } = await withRetry("getPractitionerWithMatchingValues", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PRACTITIONER_TABLE_NAME)
      .select(fullPractitionerSelect)
      .eq("group_id", group.id)
      .eq("npi_number", npiNumber)
      .is("deleted_at", null)
      .not("npi_registry_entry", "is", null)
      .order("created_at", { ascending: true });
  });

  if (error) {
    logger.error("Error getting practitioner with matching values", {
      error,
    });

    throw new Error("Error getting practitioner with matching values");
  }

  if (practitionerRows === null || practitionerRows.length === 0) {
    return null;
  }

  const targetRole = expectedRole ?? practitioner.role;
  const matchingRow = practitionerRows.find((row) => practitionerRolesMatch(row.role, targetRole));

  if (!matchingRow) {
    return null;
  }

  return mapPractitionerRowToPractitioner(matchingRow);
}, "getPractitionerWithMatchingValues");

export function practitionerRolesMatch(storedRole: string | null, targetRole: PractitionerRole | null): boolean {
  if (!targetRole || targetRole === PractitionerRole.UNKNOWN_PROVIDER) {
    return true;
  }

  if (!storedRole || storedRole === PractitionerRole.UNKNOWN_PROVIDER) {
    return true;
  }

  return storedRole === targetRole;
}

async function getPractitionerByUniqueKey(
  group: Group,
  npiNumber: string,
  role: PractitionerRole
): Promise<Practitioner | null> {
  const supabaseClient = await getSupabaseClient();
  const { data, error } = await supabaseClient
    .from(PRACTITIONER_TABLE_NAME)
    .select(fullPractitionerSelect)
    .eq("group_id", group.id)
    .eq("npi_number", npiNumber)
    .eq("role", role)
    .is("deleted_at", null)
    .maybeSingle();

  if (error) {
    logAndThrow("Error fetching practitioner after identity unique constraint violation", {
      groupId: group.id,
      npiNumber,
      role,
      error,
    });
  }

  return data ? mapPractitionerRowToPractitioner(data) : null;
}

async function createOrUpdatePractitionerImpl(
  practitioner: Practitioner,
  group: Group,
  expectedRole?: PractitionerRole,
  options?: ReceivingRosterCreateOptions
): Promise<Practitioner | null> {
  if (!group) {
    throw new Error("Group not provided for practitioner");
  }

  if (!practitioner.groupId) {
    practitioner.groupId = group.id;
  }

  if (
    practitioner.npiRegistryEntry &&
    isNpiRegistryResult(practitioner.npiRegistryEntry) &&
    practitioner.npiRegistryEntry.enumeration_type !== "NPI-1"
  ) {
    logAndThrow(
      "Cannot save practitioner with organization NPI",
      {
        npiNumber: practitioner.npiNumber,
        enumerationType: practitioner.npiRegistryEntry.enumeration_type,
      },
      true
    );
  }

  logger.info("Creating or updating practitioner", {
    practitioner,
  });

  if (!practitioner.npiNumber) {
    logger.info("No NPI number provided for practitioner", {
      practitioner,
    });
  }

  const foundPractitioner = await getPractitionerWithMatchingValues(practitioner, group, expectedRole);

  if (!foundPractitioner) {
    const isReceivingProviderCreate =
      practitioner.role === PractitionerRole.RECEIVING_PROVIDER || expectedRole === PractitionerRole.RECEIVING_PROVIDER;

    if (isReceivingProviderCreate) {
      if (!options?.allowReceivingProviderCreate) {
        logger.info("createOrUpdatePractitioner: receiving creation skipped (non-roster caller)", {
          role: practitioner.role,
          expectedRole,
          groupId: group.id,
        });

        return null;
      }

      practitioner = {
        ...practitioner,
        isApproved: true,
      };
    }

    logger.info("Creating practitioner", {
      practitioner,
    });

    try {
      return await createPractitioner(practitioner, group);
    } catch (error: unknown) {
      if (hasPostgreSqlErrorCode(error, PostgreSqlErrorCode.UNIQUE_VIOLATION) && practitioner.npiNumber) {
        const existingPractitioner = await getPractitionerByUniqueKey(group, practitioner.npiNumber, practitioner.role);

        if (!existingPractitioner) {
          logAndThrow(
            "Practitioner identity conflict occurred but the existing practitioner was not found",
            {
              groupId: group.id,
              npiNumber: practitioner.npiNumber,
              role: practitioner.role,
            },
            true
          );
        }

        logger.info("Practitioner identity conflict resolved with the existing practitioner", {
          groupId: group.id,
          npiNumber: practitioner.npiNumber,
          role: practitioner.role,
          existingPractitionerId: existingPractitioner.id,
        });

        return existingPractitioner;
      }

      throw error;
    }
  }

  logger.info("Practitioner dedupe: Found practitioner with matching NPI, no-op", {
    newPractitioner: practitioner,
    existingPractitioner: foundPractitioner,
    expectedRole,
  });

  return foundPractitioner;
}

export const createOrUpdatePractitioner = withDurationMetric(async function createOrUpdatePractitioner(
  practitioner: Practitioner,
  group: Group,
  expectedRole?: PractitionerRole,
  options?: ReceivingRosterCreateOptions
): Promise<Practitioner | null> {
  return createOrUpdatePractitionerImpl(practitioner, group, expectedRole, options);
}, "createOrUpdatePractitioner");

export const createOrUpdatePractitionerFromSor = withDurationMetric(async function createOrUpdatePractitionerFromSor(
  practitioner: Practitioner,
  group: Group,
  options?: ReceivingRosterCreateOptions
): Promise<Practitioner | null> {
  return createOrUpdatePractitionerImpl(practitioner, group, undefined, options);
}, "createOrUpdatePractitionerFromSor");

/**
 * Get all practitioners
 * @param group - The group to get the practitioners for
 * @param role - The role to filter the practitioners by
 * @returns The practitioners
 */
export const getAllPractitioners = withDurationMetric(async function getAllPractitioners(
  group: Group,
  role?: PractitionerRole
): Promise<Record<string, Practitioner>> {
  if (!group) {
    throw new Error("No group provided");
  }

  const { data: practitionerRows, error } = await withRetry("getAllPractitionersForGroup", async () => {
    const supabaseClient = await getSupabaseClient();

    const query = supabaseClient
      .from(PRACTITIONER_TABLE_NAME)
      .select(fullPractitionerSelect)
      .eq("group_id", group.id)
      .is("deleted_at", null)
      .not("npi_number", "is", null)
      .not("npi_registry_entry", "is", null)
      .order("created_at", { ascending: true });

    if (role) {
      query.eq("role", role);
    }

    return query;
  });

  if (error) {
    logger.error("Error getting practitioners", {
      error,
    });
    throw new Error("Error fetching practitioners");
  }

  if (!practitionerRows) {
    logger.error("No practitioners found for group:", {
      error,
    });

    return {};
  }

  logger.info("Successfully fetched practitioners", {
    payloadSize: JSON.stringify(practitionerRows).length,
  });

  return mapUniquePractitioners(practitionerRows);
}, "getAllPractitioners");

export const getPractitionerMapBySecondaryEhrId = withDurationMetric(async function getPractitionerMapBySecondaryEhrId(
  group: Group
): Promise<Map<string, string>> {
  const { data: practitionerRows, error } = await withRetry("getPractitionerMapBySecondaryEhrId", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PRACTITIONER_TABLE_NAME)
      .select("id,secondary_ehr_id")
      .eq("group_id", group.id)
      .is("deleted_at", null)
      .not("secondary_ehr_id", "is", null)
      .order("created_at", { ascending: true });
  });

  if (error) {
    logAndThrow("Error getting practitioner secondary EHR IDs", {
      error,
    });
  }

  const practitionerIdBySecondaryEhrId = new Map<string, string>();

  // Data might be returned as null or undefined, so we need to check for that.
  for (const practitioner of practitionerRows ?? []) {
    if (practitioner.secondary_ehr_id) {
      practitionerIdBySecondaryEhrId.set(practitioner.secondary_ehr_id, practitioner.id);
    }
  }

  return practitionerIdBySecondaryEhrId;
}, "getPractitionerMapBySecondaryEhrId");

/**
 * Get all practitioners without an EHR ID
 * @param group - The group to get the practitioners for
 * @param role - The role to filter the practitioners by
 * @returns The practitioners
 */
export const getAllWithoutEhrIdPractitioners = withDurationMetric(async function getAllWithoutEhrIdPractitioners(
  group: Group,
  role?: PractitionerRole,
  type: "primary" | "secondary" = "primary"
): Promise<Record<string, Practitioner>> {
  if (!group) {
    throw new Error("No group provided");
  }

  const { data: practitionerRows, error } = await withRetry("getAllWithoutEhrIdPractitioners", async () => {
    const supabaseClient = await getSupabaseClient();

    const query = supabaseClient
      .from(PRACTITIONER_TABLE_NAME)
      .select(fullPractitionerSelect)
      .eq("group_id", group.id)
      .is("deleted_at", null)
      .is(type === "primary" ? "ehr_id" : "secondary_ehr_id", null)
      .not("npi_number", "is", null)
      .not("npi_registry_entry", "is", null)
      .order("created_at", { ascending: true });

    if (role) {
      query.eq("role", role);
    }

    return query;
  });

  if (error) {
    logger.error("Error getting practitioners", {
      error,
    });
    throw new Error("Error fetching practitioners");
  }

  if (!practitionerRows) {
    logger.error("No practitioners found for group:", {
      error,
    });

    return {};
  }

  logger.info("Successfully fetched practitioners", {
    payloadSize: JSON.stringify(practitionerRows).length,
  });

  return mapUniquePractitioners(practitionerRows);
}, "getAllWithoutEhrIdPractitioners");

/**
 * Get all practitioners by patient id
 * @param patientId - The id of the patient
 * @param group - The group to get the practitioners for
 * @returns The practitioners
 */
export const getAllPractitionersByPatientId = withDurationMetric(async function getAllPractitionersByPatientId(
  patientId: string,
  group: Group
): Promise<Record<string, Practitioner>> {
  // get all referal provider ids
  const supabaseClient = await getSupabaseClient();

  const { data: referrals, error: referralError } = await supabaseClient
    .from("referral")
    .select("referring_provider_id,receiving_provider_id")
    .eq("patient_id", patientId);

  if (referralError) {
    logger.error("Error getting referrals by patient id", {
      patientId,
      error: referralError,
    });
    throw new Error("Error getting practitioners by patient id");
  }

  const practitionerIds = (referrals ?? []).flatMap((r) =>
    [r.receiving_provider_id, r.referring_provider_id].filter((id) => id !== null)
  );

  const { getOrderingProviderIdsForPatient } = await import("@/src/repo/patient-order");
  const orderProviderIds = await getOrderingProviderIdsForPatient(patientId, group);
  const allPractitionerIds = Array.from(new Set([...practitionerIds, ...orderProviderIds]));

  if (allPractitionerIds.length === 0) {
    return {};
  }

  const { data: practitionerRows, error } = await withRetry("getAllPractitionersByPatientId", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PRACTITIONER_TABLE_NAME)
      .select(fullPractitionerSelect)
      .eq("group_id", group.id)
      .is("deleted_at", null)
      .in("id", allPractitionerIds)
      .order("created_at", { ascending: false });
  });

  if (error) {
    logger.error("Error getting practitioners", {
      patientId,
      error,
    });
    throw new Error("Error fetching practitioners");
  }

  if (!practitionerRows) {
    logger.error("No practitioners found for patient:", {
      patientId,
      error,
    });

    return {};
  }

  const practitionersToReturn = practitionerRows.reduce((acc: Record<string, Practitioner>, practitioner: any) => {
    acc[practitioner.id] = mapPractitionerRowToPractitioner(practitioner);

    return acc;
  }, {});

  return practitionersToReturn;
}, "getAllPractitionersByPatientId");

/**
 * Get a practitioner by NPI number
 * @param npiNumber - The NPI number to get the practitioner by
 * @param group - The group to get the practitioner from
 * @returns The practitioner
 */
export const getPractitionerByNpiNumber = withDurationMetric(async function getPractitionerByNpiNumber(
  npiNumber: string,
  group: Group
): Promise<Practitioner | null> {
  const { data: practitionerRows, error } = await withRetry("getPractitionerByNpiNumber", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PRACTITIONER_TABLE_NAME)
      .select(fullPractitionerSelect)
      .eq("npi_number", npiNumber)
      .eq("group_id", group.id)
      .is("deleted_at", null)
      .order("created_at", { ascending: true });
  });

  if (error) {
    logger.error("Error getting practitioner by NPI number", {
      error,
    });

    throw new Error("Error getting practitioner by NPI number");
  }

  if (practitionerRows == null) {
    return null;
  }

  if (practitionerRows.length === 0) {
    return null;
  }

  const practitionerToReturn = mapPractitionerRowToPractitioner(practitionerRows[0]);

  return practitionerToReturn;
}, "getPractitionerByNpiNumber");

/**
 * Get all practitioners by NPI numbers
 * @param npiNumbers - The NPI numbers to get the practitioners by
 * @param role - The role to filter the practitioners by
 * @param group - The group to get the practitioners from
 * @returns The practitioners
 */
export const getAllPractitionersByNpiNumbers = withDurationMetric(async function getAllPractitionersByNpiNumbers(
  npiNumbers: string[],
  role: PractitionerRole | null = null,
  group: Group
): Promise<Practitioner[]> {
  const { data: practitionerRows, error } = await withRetry("getAllPractitionersByNpiNumbers", async () => {
    const supabaseClient = await getSupabaseClient();

    const query = supabaseClient
      .from(PRACTITIONER_TABLE_NAME)
      .select(fullPractitionerSelect)
      .in("npi_number", npiNumbers)
      .eq("group_id", group.id)
      .is("deleted_at", null)
      .order("created_at", { ascending: true });

    if (role) {
      query.eq("role", role);
    }

    return query;
  });

  if (error) {
    logger.error("Error getting practitioners by NPI numbers", {
      error,
    });

    throw new Error("Error getting practitioners by NPI numbers");
  }

  if (practitionerRows == null) {
    return [];
  }

  if (practitionerRows.length === 0) {
    return [];
  }

  const practitionersToReturn = practitionerRows.map((practitioner) => mapPractitionerRowToPractitioner(practitioner));

  return practitionersToReturn;
}, "getAllPractitionersByNpiNumbers");

/**
 * Update the EHR ID of a practitioner
 * @param practitioner - The practitioner to update
 * @param ehrId - The EHR ID to update the practitioner with
 * @returns The updated practitioner
 */
export const updateEhrId = withDurationMetric(async function updateEhrId(
  practitioner: Practitioner,
  ehrId: string,
  type: "primary" | "secondary" = "primary"
): Promise<Practitioner> {
  const { data: _practitionerRows, error } = await withRetry("updateEhrId", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PRACTITIONER_TABLE_NAME)
      .update(type === "primary" ? { ehr_id: ehrId } : { secondary_ehr_id: ehrId })
      .eq("id", practitioner.id);
  });

  if (error) {
    logger.error("Error updating EHR ID", {
      error,
    });

    throw new Error("Error updating EHR ID");
  }

  logger.info("Updated EHR ID", {
    practitionerId: practitioner.id,
    ehrId,
  });

  return practitioner;
}, "updateEhrId");

/**
 * Delete a practitioner
 * @param practitionerId - The ID of the practitioner to delete
 * @param group - The group to delete the practitioner for
 * @returns True if successful
 */
export const deletePractitioner = withDurationMetric(async function deletePractitioner(
  { practitionerId }: { practitionerId: string },
  group: Group
): Promise<boolean> {
  logger.info(`Deleting practitioner ${practitionerId} for group ${group.id}`, {
    practitionerId,
    groupId: group.id,
  });

  const { data: _data, error } = await withRetry("deletePractitioner", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PRACTITIONER_TABLE_NAME)
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", practitionerId)
      .eq("group_id", group.id);
  });

  if (error) {
    logger.error(`Error deleting practitioner with ID ${practitionerId}:`, {
      error,
      practitionerId,
    });
    throw new Error("Error deleting practitioner");
  }

  logger.info(`Deleted practitioner with ID ${practitionerId}`, {
    practitionerId,
  });

  return true;
}, "deletePractitioner");

/**
 * Get a practitioner by EHR ID
 * @param ehrId - The EHR ID of the practitioner
 * @param group - The group to get the practitioner for
 * @returns The practitioner
 */
export const getPractitionerByEhrId = withDurationMetric(async function getPractitionerByEhrId(
  ehrId: string,
  group: Group
): Promise<Practitioner | null> {
  const { data: practitionerRows, error } = await withRetry("getPractitionerByEhrId", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PRACTITIONER_TABLE_NAME)
      .select(fullPractitionerSelect)
      .eq("ehr_id", ehrId)
      .eq("group_id", group.id)
      .is("deleted_at", null);
  });

  if (error) {
    logger.error("Error getting practitioner by EHR ID", {
      error,
    });

    throw new Error("Error getting practitioner by EHR ID");
  }

  if (practitionerRows == null || practitionerRows.length === 0) {
    return null;
  }

  return mapPractitionerRowToPractitioner(practitionerRows[0]);
}, "getPractitionerByEhrId");

/**
 * Ordering providers with an Athena EHR id for a group (does not require npi_registry_entry).
 * Used to build ConvenientMD Athena provider maps at runtime.
 */
export const getOrderingPractitionersWithEhrId = withDurationMetric(async function getOrderingPractitionersWithEhrId(
  group: Group
): Promise<Practitioner[]> {
  const { data: practitionerRows, error } = await withRetry("getOrderingPractitionersWithEhrId", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PRACTITIONER_TABLE_NAME)
      .select(fullPractitionerSelect)
      .eq("group_id", group.id)
      .eq("role", PractitionerRole.ORDERING_PROVIDER)
      .not("ehr_id", "is", null)
      .is("deleted_at", null);
  });

  if (error) {
    logAndThrow("Error getting ordering practitioners with EHR ID", {
      error,
      groupId: group.id,
    });
  }

  return (practitionerRows ?? []).map(mapPractitionerRowToPractitioner);
}, "getOrderingPractitionersWithEhrId");

/**
 * Find a receiving provider whose MRN is stored in either `ehr_id` or `secondary_ehr_id`.
 * Use when the lookup value is an MRN from the EHR (e.g. Cobalt `provider_ehr_id`) and the
 * group's column convention is not known at query time.
 */
export const getReceivingProviderByMrn = withDurationMetric(async function getReceivingProviderByMrn(
  mrn: string,
  group: Group
): Promise<Practitioner | null> {
  const trimmedMrn = mrn.trim();

  if (!trimmedMrn) {
    return null;
  }

  const { data: practitionerRows, error } = await withRetry("getReceivingProviderByMrn", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PRACTITIONER_TABLE_NAME)
      .select(fullPractitionerSelect)
      .eq("group_id", group.id)
      .eq("role", PractitionerRole.RECEIVING_PROVIDER)
      .is("deleted_at", null)
      .or(`ehr_id.eq.${trimmedMrn},secondary_ehr_id.eq.${trimmedMrn}`);
  });

  if (error) {
    logger.error("Error getting receiving provider by MRN", {
      error,
      mrn: trimmedMrn,
      groupId: group.id,
    });

    throw new Error("Error getting receiving provider by MRN");
  }

  if (practitionerRows == null || practitionerRows.length === 0) {
    return null;
  }

  return mapPractitionerRowToPractitioner(practitionerRows[0]);
}, "getReceivingProviderByMrn");
